import { useEffect, useState } from "react";
import { getProfitLoss } from "../api/finance.ts";
import { useAuth } from "../context/auth.tsx";
import { Layout } from "../components/layout.tsx";

interface ProfitLoss {
  period: { from: string; to: string };
  revenue: number;
  expenses: number;
  grossProfit: number;
  profitMargin: number;
  invoiceCount: number;
  paidInvoiceCount: number;
  overdueInvoiceCount: number;
  outstandingAmount: number;
  totalPaymentsReceived: number;
  expensesByCategory: Array<{ category: string; total: number; count: number }>;
  revenueByMonth: Array<{ month: string; total_revenue: number; invoice_count: number }>;
}

function currency(n: number) {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(n);
}

export function FinanceReportsPage() {
  const { token } = useAuth();
  const [report, setReport] = useState<ProfitLoss | null>(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState("90");

  const load = (d: string) => {
    setLoading(true);
    const from = new Date(Date.now() - parseInt(d) * 86400000).toISOString();
    void getProfitLoss(token, { from }).then((r) => {
      setReport(r as ProfitLoss);
      setLoading(false);
    });
  };

  useEffect(() => {
    load(days);
  }, []);

  return (
    <Layout>
      <div className="mx-auto max-w-6xl p-6">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-2xl font-bold text-white">Financial Reports</h1>
          <div className="flex gap-2">
            {["30", "90", "180", "365"].map((d) => (
              <button
                key={d}
                onClick={() => {
                  setDays(d);
                  load(d);
                }}
                className={`rounded px-3 py-1.5 text-sm ${days === d ? "bg-indigo-600 text-white" : "bg-gray-800 text-gray-400 hover:text-white"}`}
              >
                {d}d
              </button>
            ))}
          </div>
        </div>
        {loading ? (
          <p className="text-gray-400">Generating report...</p>
        ) : report ? (
          <>
            <div className="mb-8 grid grid-cols-2 gap-4 md:grid-cols-4">
              {[
                { label: "Revenue", value: currency(report.revenue), color: "text-green-400" },
                { label: "Expenses", value: currency(report.expenses), color: "text-red-400" },
                {
                  label: "Gross Profit",
                  value: currency(report.grossProfit),
                  color: report.grossProfit >= 0 ? "text-green-400" : "text-red-400",
                },
                {
                  label: "Profit Margin",
                  value: `${(report.profitMargin * 100).toFixed(1)}%`,
                  color: "text-blue-400",
                },
                { label: "Total Invoices", value: report.invoiceCount, color: "text-gray-300" },
                { label: "Paid Invoices", value: report.paidInvoiceCount, color: "text-green-400" },
                {
                  label: "Overdue",
                  value: report.overdueInvoiceCount,
                  color: report.overdueInvoiceCount > 0 ? "text-red-400" : "text-gray-400",
                },
                {
                  label: "Outstanding",
                  value: currency(report.outstandingAmount),
                  color: "text-yellow-400",
                },
              ].map((s) => (
                <div key={s.label} className="rounded-lg border border-gray-800 bg-gray-900 p-4">
                  <p className="text-xs text-gray-400">{s.label}</p>
                  <p className={`mt-1 text-xl font-bold ${s.color}`}>{s.value}</p>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div className="rounded-lg border border-gray-800 bg-gray-900 p-4">
                <h2 className="mb-3 text-sm font-medium text-gray-300">Expenses by Category</h2>
                <div className="space-y-2">
                  {report.expensesByCategory.length === 0 ? (
                    <p className="text-sm text-gray-500">No data</p>
                  ) : (
                    report.expensesByCategory.map((e) => (
                      <div key={e.category} className="flex items-center justify-between text-sm">
                        <span className="text-gray-300">{e.category}</span>
                        <div className="text-right">
                          <span className="text-white">{currency(e.total)}</span>
                          <span className="ml-2 text-xs text-gray-500">{e.count} items</span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
              <div className="rounded-lg border border-gray-800 bg-gray-900 p-4">
                <h2 className="mb-3 text-sm font-medium text-gray-300">Revenue by Month</h2>
                <div className="space-y-2">
                  {report.revenueByMonth.length === 0 ? (
                    <p className="text-sm text-gray-500">No data</p>
                  ) : (
                    report.revenueByMonth.map((r) => (
                      <div key={r.month} className="flex items-center justify-between text-sm">
                        <span className="text-gray-400">{r.month}</span>
                        <div className="text-right">
                          <span className="text-white">{currency(r.total_revenue)}</span>
                          <span className="ml-2 text-xs text-gray-500">{r.invoice_count} inv</span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </>
        ) : null}
      </div>
    </Layout>
  );
}
